import type { CSSProperties, ReactNode, Ref } from 'react'
import { forwardRef } from 'react'

import { useSlotProps } from '@adverity/ds-core/slot'
import { ConditionalWrapWithTooltipCompat as ConditionalWrapWithTooltip } from '@adverity/ds-core/tooltip/compat'
import { cn } from '@adverity/ds-tokens/cn'
import { colors } from '@adverity/ds-tokens/colors'

import { CornerIconGroup } from './corner-icon-group'
import type { ColorToken, IconProps, PrivateIconProps, SchemaMapping, Size, Variant } from './icon-props'
import './icon.css'

const variantColorMap: Record<Variant | SchemaMapping | 'brand', string> = {
    danger: 'text-danger-active',
    brand: 'text-brand-highlighted',
    success: 'text-success-highlighted',
    warning: 'text-warning-highlighted',
    dimension: 'text-dimension-highlighted',
    metric: 'text-metric-highlighted',
}

const sizeValueMap: Record<Size, string> = {
    'x-small': '16px',
    small: '24px',
    medium: '32px',
    large: '40px',
    'x-large': '48px',
}

const parseViewBox = (viewBox: string) => {
    const match = /0 0 (\d+) (\d+)/.exec(viewBox)

    return {
        width: match ? Number.parseInt(match[1] as string, 10) : 24,
        height: match ? Number.parseInt(match[2] as string, 10) : 24,
    }
}

const camelToKebab = (str: string): string =>
    str
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/([a-zA-Z])(\d)/g, '$1-$2')
        .toLowerCase()

const getColorClass = (variant: IconProps['variant'], color: string | undefined): string | undefined => {
    if (variant) return variantColorMap[variant]

    if (color && color !== 'current') return `text-${camelToKebab(color)}`

    return undefined
}

const getColorStyle = (
    variant: IconProps['variant'],
    color: string | undefined,
    colorClass: string | undefined,
): string | undefined => {
    if (variant && colorClass) return `var(--color-${colorClass.slice(5)})`

    if (color && color !== 'current') return colors[color as ColorToken] as string | undefined

    return undefined
}

const getIconClassName = ({
    keepFillColor,
    colorClass,
    tooltip,
    className,
}: {
    keepFillColor: boolean | undefined
    colorClass: string | undefined
    tooltip: string | undefined
    className: string | undefined
}) =>
    cn(
        'ds-icon-sized',
        'inline-block shrink-0',
        !keepFillColor && 'ds-icon',
        colorClass,
        tooltip && 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        className,
    )

export const Icon = forwardRef<SVGElement, IconProps & PrivateIconProps>((props, ref) => {
    const {
        rotate,
        size = 'small',
        color: colorProp,
        variant,
        viewBox = '0 0 24 24',
        tooltip,
        children,
        keepFillColor,
        cornerIcons,
        cornerIconSize = 12,
        cornerIconBackgroundColor = 'background',
        cornerIconBackgroundShape = 'circle',
        className,
        slot: _slotIgnored,
        style: styleProp,
        ...rest
    } = useSlotProps(props, 'icon')

    const { width: viewBoxWidth, height: viewBoxHeight } = parseViewBox(viewBox)
    const colorClass = keepFillColor ? undefined : getColorClass(variant, colorProp)
    const colorValue = getColorStyle(variant, colorProp, colorClass)

    const mergedStyle = {
        '--ds-icon-size': sizeValueMap[size],
        ...(colorValue ? { color: colorValue } : undefined),
        ...(rotate ? { transform: `rotate(${rotate}deg)` } : undefined),
        ...(styleProp as CSSProperties),
    } as CSSProperties

    return (
        <ConditionalWrapWithTooltip tooltip={tooltip}>
            <svg
                ref={ref as Ref<SVGSVGElement>}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox={viewBox}
                overflow="visible"
                className={getIconClassName({ keepFillColor, colorClass, tooltip, className })}
                style={mergedStyle}
                tabIndex={tooltip ? 0 : undefined}
                {...rest}
            >
                {children as ReactNode}
                <CornerIconGroup
                    cornerIcons={cornerIcons}
                    viewBoxWidth={viewBoxWidth}
                    viewBoxHeight={viewBoxHeight}
                    cornerIconSize={cornerIconSize}
                    cornerIconBackgroundColor={cornerIconBackgroundColor}
                    cornerIconBackgroundShape={cornerIconBackgroundShape}
                />
            </svg>
        </ConditionalWrapWithTooltip>
    )
})

Icon.displayName = 'Icon'

export type { ColorToken, IconProps } from './icon-props'
